import { useEffect, useRef, useState } from "react";
import { ChevronDownIcon } from "lucide-react";
import RoundInfo from "./RoundInfo";
import { getTournamentTheme } from "../../../../../styles/themes";

export default function TournamentStandings({
    prevStandings,
    players,
    rounds,
    tournamentModes,
    tournament,
    setTournament,
}) {
    const [activeRoundIndex, setActiveRoundIndex] = useState(0);
    const [expandedRoundIndex, setExpandedRoundIndex] = useState(null);
    const [expandedMobilePlayerId, setExpandedMobilePlayerId] = useState(null);
    const [expandedTeamKey, setExpandedTeamKey] = useState(null);
    const [roundInfoPlayerRequest, setRoundInfoPlayerRequest] = useState(null);
    const roundInfoRequestCounterRef = useRef(0);
    const [teamStandings, setTeamStandings] = useState([]);
    const [isStickyTotal, setIsStickyTotal] = useState(false);
    const [isMobile, setIsMobile] = useState(
        () => typeof window !== "undefined" && window.innerWidth < 768
    );
    const [availableTableWidth, setAvailableTableWidth] = useState(0);
    const leaderboardSectionRef = useRef(null);
    const roundInfoSectionRef = useRef(null);
    const theme = getTournamentTheme(tournament?.theme_key);

    useEffect(() => {
        const updateLayout = () => {
            const mobile = window.innerWidth < 768;

            setIsMobile(mobile);
            setAvailableTableWidth(
                leaderboardSectionRef.current?.clientWidth || window.innerWidth
            );
            setIsStickyTotal(rounds.length >= 5);
        };

        updateLayout();
        const frameId = window.requestAnimationFrame(updateLayout);
        window.addEventListener("resize", updateLayout);

        return () => {
            window.cancelAnimationFrame(frameId);
            window.removeEventListener("resize", updateLayout);
        };
    }, [rounds.length]);

    const getDeduction = (player) => {
        if (!player || !tournament.miniGames?.length) return 0;

        let deduction = 0;

        tournament.miniGames.forEach((miniGame) => {
            if (!miniGame.subtract_from_score) return;

            (tournament.rounds || []).forEach((_, roundIdx) => {
                const value = player?.miniGameScores?.[roundIdx]?.[miniGame.name];
                if (value) {
                    deduction += value * (miniGame.deduction_value || 0);
                }
            });
        });

        return deduction;
    };

    const getMiniGameWins = (player) => {
        if (!player || !tournament.miniGames?.length) return 0;

        return tournament.miniGames.reduce((total, miniGame) => {
            const wins = (tournament.rounds || []).reduce(
                (roundTotal, _, roundIdx) =>
                    roundTotal + Number(
                        player?.miniGameScores?.[roundIdx]?.[miniGame.name] || 0
                    ),
                0
            );
            return total + wins;
        }, 0);
    };

    const getRoundDeduction = (player, roundIndex) => {
        if (!player || !tournament.miniGames?.length) return 0;

        return tournament.miniGames.reduce((total, miniGame) => {
            if (!miniGame.subtract_from_score) return total;

            const wins = Number(
                player?.miniGameScores?.[roundIndex]?.[miniGame.name] || 0
            );

            return total + wins * Number(miniGame.deduction_value || 0);
        }, 0);
    };

    const getRoundMiniGameWins = (player, roundIndex) => {
        if (!player || !tournament.miniGames?.length) return 0;

        return tournament.miniGames.reduce(
            (total, miniGame) =>
                total +
                Number(
                    player?.miniGameScores?.[roundIndex]?.[miniGame.name] || 0
                ),
            0
        );
    };

    const normalizeMode = (value) =>
        String(value || "")
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .toLocaleLowerCase("sv-SE")
            .replace(/[^a-z0-9]+/g, " ")
            .trim();

    const isIndividualHandicapRound = (round, roundIndex) => {
        const mode = normalizeMode(getRoundMode(round, roundIndex));
        const mentionsHandicap =
            mode.includes("handicap") ||
            mode.split(" ").includes("hcp");

        return (
            mentionsHandicap &&
            mode.includes("slag") &&
            !mode.includes("scramble")
        );
    };

    const getPlayerRoundHandicap = (player, round) => {
        const fullHandicap = Number.parseFloat(player?.handicap);

        if (!Number.isFinite(fullHandicap)) return 0;

        return (round?.holes ?? 18) === 9
            ? fullHandicap / 2
            : fullHandicap;
    };

    const getRoundGrossScore = (player, round, roundIndex) => {
        const netScore = Number(player?.scores?.[roundIndex]);

        if (!isIndividualHandicapRound(round, roundIndex) || !(netScore > 0)) {
            return null;
        }

        // Nettoresultatet sparas som heltal. Återskapa därför bruttot på
        // samma sätt som i resultatregistreringen, så inga decimaler visas.
        return Math.round(netScore + getPlayerRoundHandicap(player, round));
    };

    const renderStars = (count) => {
        if (!count) return null;
        const visible = Math.min(count, 5);

        return (
            <span
                className={`${theme.accent} inline-flex shrink-0 items-center gap-0.5 text-xs`}
                title={`${count} sidotävlings-vinst${count === 1 ? "" : "er"}`}
                aria-label={`${count} sidotävlings-vinst${count === 1 ? "" : "er"}`}
            >
                <span aria-hidden="true">{"★".repeat(visible)}</span>
                {count > visible && (
                    <span className="text-[9px] font-semibold">+{count - visible}</span>
                )}
            </span>
        );
    };

    const renderCompactStars = (count, deduction = 0) => {
        if (!count) return null;

        const deductionLabel = deduction > 0 ? `, ${deduction} slags avdrag` : "";

        return (
            <span
                className="inline-flex items-center gap-1 whitespace-nowrap text-[10px] font-semibold"
                title={`${count} sidotävlings-vinst${count === 1 ? "" : "er"}${deductionLabel}`}
                aria-label={`${count} sidotävlings-vinst${count === 1 ? "" : "er"}${deductionLabel}`}
            >
                <span
                    aria-hidden="true"
                    className={`${theme.accent} tracking-[-0.06em]`}
                >
                    {"★".repeat(count)}
                </span>
                {deduction > 0 && (
                    <span className={theme.positive}>(-{deduction})</span>
                )}
            </span>
        );
    };

    const getRawTotal = (player) =>
        (player?.scores || []).reduce(
            (sum, value) => sum + (value || 0),
            0
        );

    const getTotalWithDeduction = (player) => {
        if (!player) return 0;
        return getRawTotal(player) - getDeduction(player);
    };

    const sortedPlayers = [...(tournament.playerData || [])].sort(
        (a, b) => getTotalWithDeduction(a) - getTotalWithDeduction(b)
    );

    const finalRoundIndex = rounds.length - 1;
    const finalRoundHasScores = (players || []).some(
        (player) =>
            typeof player.scores?.[finalRoundIndex] === "number" &&
            player.scores[finalRoundIndex] > 0
    );

    const podium = sortedPlayers.slice(0, 3);
    const activeRound = tournament.rounds?.[activeRoundIndex];
    const hasDeductions = tournament.miniGames?.some(
        (miniGame) => miniGame.subtract_from_score
    );

    const roundCount = rounds.length;
    const isSingleRound = roundCount === 1;

    // Rundnamnen visas direkt bara när hela tabellen beräknas få plats
    // utan horisontell scroll. Annars används kompakta sifferknappar.
    const estimatedTableWidth =
        64 +
        180 +
        roundCount * 140 +
        (hasDeductions ? 76 : 0) +
        82;
    const showRoundNamesInHeader =
        !isMobile &&
        roundCount <= 3 &&
        availableTableWidth >= estimatedTableWidth;

    const handleRoundHeaderClick = (roundIndex) => {
        setActiveRoundIndex(roundIndex);
        setExpandedRoundIndex((current) =>
            current === roundIndex ? null : roundIndex
        );
    };

    const scrollToRoundInfo = () => {
        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                roundInfoSectionRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });
            });
        });
    };

    const handleDetailedRoundClick = (roundIndex, playerId = null) => {
        setActiveRoundIndex(roundIndex);
        setExpandedRoundIndex(null);
        setExpandedMobilePlayerId(null);

        if (playerId != null) {
            roundInfoRequestCounterRef.current += 1;
            setRoundInfoPlayerRequest({
                playerId,
                roundIndex,
                requestId: roundInfoRequestCounterRef.current,
            });
        } else {
            setRoundInfoPlayerRequest(null);
        }

        scrollToRoundInfo();
    };

    const getRoundName = (round, index) =>
        round?.name || rounds[index] || `Runda ${index + 1}`;

    const getRoundMode = (round, index) =>
        round?.modes?.[0] ||
        tournamentModes?.[index] ||
        "Spelform saknas";

    const getRoundPosition = (playerId, roundIndex) => {
        const round = tournament.rounds?.[roundIndex];
        const mode = getRoundMode(round, roundIndex).toLowerCase();
        const isScrambleRound = mode.includes("scramble");

        if (
            isScrambleRound &&
            Array.isArray(round?.teams) &&
            round.teams.length > 0
        ) {
            const scoredTeams = round.teams
                .map((team) => {
                    const scoringPlayer = (team || [])
                        .map((memberId) =>
                            (tournament.playerData || []).find(
                                (player) =>
                                    String(player.id) === String(memberId)
                            )
                        )
                        .find(
                            (player) =>
                                Number(player?.scores?.[roundIndex]) > 0
                        );

                    return {
                        team: team || [],
                        score: Number(scoringPlayer?.scores?.[roundIndex]),
                    };
                })
                .filter((entry) => Number.isFinite(entry.score) && entry.score > 0);

            const playerTeam = scoredTeams.find((entry) =>
                entry.team.some(
                    (memberId) => String(memberId) === String(playerId)
                )
            );

            if (!playerTeam) return null;

            const betterTeams = scoredTeams.filter(
                (entry) => entry.score < playerTeam.score
            );

            return betterTeams.length + 1;
        }

        const player = (tournament.playerData || []).find(
            (item) => String(item.id) === String(playerId)
        );
        const playerScore = Number(player?.scores?.[roundIndex]);

        if (!Number.isFinite(playerScore) || playerScore <= 0) {
            return null;
        }

        const betterPlayers = (tournament.playerData || []).filter((item) => {
            const score = Number(item?.scores?.[roundIndex]);
            return (
                Number.isFinite(score) &&
                score > 0 &&
                score < playerScore
            );
        });

        return betterPlayers.length + 1;
    };

    const expandedRound =
        expandedRoundIndex !== null
            ? tournament.rounds?.[expandedRoundIndex]
            : null;

    useEffect(() => {
        if (
            tournament.team_mode &&
            tournament.playerData &&
            tournament.team_data?.assignments
        ) {
            const standings = ["red", "green"]
                .map((teamKey) => {
                    const teamPlayers = tournament.playerData.filter(
                        (player) =>
                            tournament.team_data.assignments[player.id] === teamKey
                    );

                    const playerRows = teamPlayers
                        .map((player) => {
                            const rawTotal = getRawTotal(player);
                            const deduction = getDeduction(player);
                            const miniGameWins = getMiniGameWins(player);

                            return {
                                id: player.id,
                                name: player.name,
                                rawTotal,
                                deduction,
                                miniGameWins,
                                total: rawTotal - deduction,
                            };
                        })
                        .sort((a, b) => a.total - b.total || (a.name || "").localeCompare(b.name || "", "sv-SE"));

                    const roundRows = (tournament.rounds || []).map(
                        (round, roundIndex) => {
                            const rawTotal = teamPlayers.reduce(
                                (sum, player) =>
                                    sum + Number(player?.scores?.[roundIndex] || 0),
                                0
                            );
                            const deduction = teamPlayers.reduce(
                                (sum, player) =>
                                    sum + getRoundDeduction(player, roundIndex),
                                0
                            );
                            const miniGameWins = teamPlayers.reduce(
                                (sum, player) =>
                                    sum + getRoundMiniGameWins(player, roundIndex),
                                0
                            );

                            return {
                                roundIndex,
                                name: getRoundName(round, roundIndex),
                                rawTotal,
                                deduction,
                                miniGameWins,
                                total: rawTotal - deduction,
                            };
                        }
                    );

                    const rawTotal = playerRows.reduce(
                        (sum, player) => sum + player.rawTotal,
                        0
                    );
                    const deduction = playerRows.reduce(
                        (sum, player) => sum + player.deduction,
                        0
                    );
                    const miniGameWins = playerRows.reduce(
                        (sum, player) => sum + player.miniGameWins,
                        0
                    );

                    return {
                        key: teamKey,
                        name:
                            tournament.team_data?.captains?.[teamKey] ||
                            (teamKey === "red" ? "Röd" : "Grön"),
                        color:
                            tournament.team_data?.colors?.[teamKey] ||
                            (teamKey === "red" ? "#9F3A38" : "#2F6B4F"),
                        rawTotal,
                        deduction,
                        miniGameWins,
                        total: rawTotal - deduction,
                        players: playerRows,
                        rounds: roundRows,
                    };
                })
                .sort((a, b) => a.total - b.total);

            setTeamStandings(standings);
        } else {
            setTeamStandings([]);
            setExpandedTeamKey(null);
        }
    }, [tournament]);

    const expandedTeam =
        teamStandings.find((team) => team.key === expandedTeamKey) || null;

    if (isSingleRound) {
        return (
            <div className={`mt-6 ${theme.textPrimary}`}>
                <RoundInfo
                    activeRoundIndex={0}
                    setActiveRoundIndex={setActiveRoundIndex}
                    rounds={tournament.rounds}
                    players={players}
                    activeRound={tournament.rounds?.[0]}
                    tournament={tournament}
                    setTournament={setTournament}
                    expandPlayerRequest={roundInfoPlayerRequest}
                />
            </div>
        );
    }

    return (
        <div className={`mt-6 space-y-8 ${theme.textPrimary}`}>
            {tournament.team_mode && tournament.team_data?.assignments && (
                <section className={`${theme.section} ${theme.border} overflow-hidden rounded-2xl border shadow-sm`}>
                    <div className={`${theme.border} border-b px-5 py-5 sm:px-7`}>
                        <p className={`${theme.textSecondary} text-[11px] font-semibold uppercase tracking-[0.26em]`}>
                            Lagtävling
                        </p>
                        {hasDeductions && (
                            <p className={`${theme.textSecondary} mt-1 text-xs`}>
                                Sidotävlings-avdrag är inräknade i lagens totalresultat.
                            </p>
                        )}
                    </div>

                    <div className="grid gap-3 p-4 sm:grid-cols-2 sm:p-6">
                        {teamStandings.map((team, index) => {
                            const isExpanded = expandedTeamKey === team.key;

                            return (
                                <button
                                    key={team.key}
                                    type="button"
                                    onClick={() =>
                                        setExpandedTeamKey((current) =>
                                            current === team.key ? null : team.key
                                        )
                                    }
                                    aria-expanded={isExpanded}
                                    className={`group ${theme.tableRow} ${theme.border} rounded-xl border border-l-4 px-4 py-4 text-left transition hover:bg-black/[0.025]`}
                                    style={{ borderLeftColor: team.color }}
                                >
                                    <div className="grid grid-cols-[minmax(0,1fr)_auto_28px] items-center gap-3">
                                        <div className="min-w-0">
                                            <p className={`${theme.textSecondary} text-[10px] font-semibold uppercase tracking-[0.2em]`}>
                                                Placering {index + 1}
                                            </p>
                                            <p className={`${theme.textPrimary} mt-1 truncate text-base font-semibold`}>
                                                Team {team.name}
                                            </p>
                                            <div className="mt-1 min-h-[16px]">
                                                {renderCompactStars(
                                                    team.miniGameWins,
                                                    team.deduction
                                                )}
                                            </div>
                                        </div>

                                        <div className="text-right">
                                            <p className={`${theme.textSecondary} text-[10px] font-semibold uppercase tracking-[0.18em]`}>
                                                Total
                                            </p>
                                            <p className={`${theme.total} mt-1 text-2xl font-semibold tabular-nums`}>
                                                {team.total}
                                            </p>
                                            {team.deduction > 0 && (
                                                <p className={`${theme.textSecondary} mt-0.5 whitespace-nowrap text-[9px] font-medium tabular-nums`}>
                                                    Brutto {team.rawTotal}
                                                </p>
                                            )}
                                        </div>

                                        <span
                                            aria-hidden="true"
                                            className={`${theme.accent} inline-flex h-7 w-7 items-center justify-center rounded-full transition-colors group-hover:bg-black/[0.035]`}
                                        >
                                            <ChevronDownIcon
                                                className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
                                            />
                                        </span>
                                    </div>
                                </button>
                            );
                        })}

                        {expandedTeam && (
                            <div className={`${theme.sectionMuted} ${theme.border} rounded-xl border p-4 sm:col-span-2 sm:p-5`}>
                                <div className="flex items-start justify-between gap-4">
                                    <div className="min-w-0">
                                        <p className={`${theme.textSecondary} text-[10px] font-semibold uppercase tracking-[0.2em]`}>
                                            Fördjupning
                                        </p>
                                        <p className={`${theme.textPrimary} mt-1 truncate text-base font-semibold`}>
                                            Team {expandedTeam.name}
                                        </p>
                                    </div>
                                    <span
                                        className="h-3 w-3 shrink-0 rounded-full"
                                        style={{ backgroundColor: expandedTeam.color }}
                                        aria-hidden="true"
                                    />
                                </div>

                                <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
                                    <div className={`${theme.tableRow} ${theme.border} rounded-lg border px-3 py-2.5`}>
                                        <p className={`${theme.textSecondary} text-[9px] font-semibold uppercase tracking-[0.14em]`}>
                                            Brutto
                                        </p>
                                        <p className={`${theme.textPrimary} mt-1 text-lg font-semibold tabular-nums`}>
                                            {expandedTeam.rawTotal}
                                        </p>
                                    </div>
                                    <div className={`${theme.tableRow} ${theme.border} rounded-lg border px-3 py-2.5`}>
                                        <p className={`${theme.textSecondary} text-[9px] font-semibold uppercase tracking-[0.14em]`}>
                                            Sidotävlingar
                                        </p>
                                        <div className="mt-1 min-h-[24px] flex items-center">
                                            {renderCompactStars(
                                                expandedTeam.miniGameWins,
                                                expandedTeam.deduction
                                            ) || (
                                                <span className={`${theme.textSecondary} text-sm`}>–</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className={`${theme.tableRow} ${theme.border} rounded-lg border px-3 py-2.5`}>
                                        <p className={`${theme.textSecondary} text-[9px] font-semibold uppercase tracking-[0.14em]`}>
                                            Avdrag
                                        </p>
                                        <p className={`${theme.positive} mt-1 text-lg font-semibold tabular-nums`}>
                                            {expandedTeam.deduction > 0
                                                ? `−${expandedTeam.deduction}`
                                                : "–"}
                                        </p>
                                    </div>
                                    <div className={`${theme.tableRow} ${theme.border} rounded-lg border px-3 py-2.5`}>
                                        <p className={`${theme.textSecondary} text-[9px] font-semibold uppercase tracking-[0.14em]`}>
                                            Netto
                                        </p>
                                        <p className={`${theme.total} mt-1 text-lg font-semibold tabular-nums`}>
                                            {expandedTeam.total}
                                        </p>
                                    </div>
                                </div>

                                <div className={`${theme.border} mt-5 border-t pt-4`}>
                                    <p className={`${theme.textSecondary} text-[9px] font-semibold uppercase tracking-[0.16em]`}>
                                        Runda för runda
                                    </p>
                                    <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                                        {expandedTeam.rounds.map((round) => (
                                            <div
                                                key={round.roundIndex}
                                                className={`${theme.tableRow} ${theme.border} rounded-lg border px-3 py-3`}
                                            >
                                                <div className="flex items-center justify-between gap-3">
                                                    <p className={`${theme.textPrimary} min-w-0 truncate text-xs font-semibold`}>
                                                        {round.name}
                                                    </p>
                                                    <p className={`${theme.total} shrink-0 text-sm font-semibold tabular-nums`}>
                                                        {round.rawTotal > 0 ? round.total : "–"}
                                                    </p>
                                                </div>
                                                <div className="mt-1.5 flex min-h-[16px] items-center justify-between gap-2">
                                                    <span className={`${theme.textSecondary} text-[9px] tabular-nums`}>
                                                        {round.rawTotal > 0
                                                            ? `Brutto ${round.rawTotal}`
                                                            : "Ej spelad"}
                                                    </span>
                                                    {round.miniGameWins > 0 &&
                                                        renderCompactStars(
                                                            round.miniGameWins,
                                                            round.deduction
                                                        )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className={`${theme.border} mt-5 border-t pt-4`}>
                                    <p className={`${theme.textSecondary} text-[9px] font-semibold uppercase tracking-[0.16em]`}>
                                        Spelarnas bidrag
                                    </p>
                                    <div className={`${theme.border} mt-2 overflow-hidden rounded-lg border`}>
                                        {expandedTeam.players.map((player, index) => (
                                            <div
                                                key={player.id}
                                                className={`grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 px-3 py-2.5 ${
                                                    index % 2 === 0
                                                        ? theme.tableRow
                                                        : theme.tableRowAlternate
                                                } ${index > 0 ? `border-t ${theme.border}` : ""}`}
                                            >
                                                <div className="min-w-0">
                                                    <p className={`${theme.textPrimary} truncate text-xs font-medium`}>
                                                        {player.name}
                                                    </p>
                                                    <div className="mt-0.5 min-h-[15px]">
                                                        {renderCompactStars(
                                                            player.miniGameWins,
                                                            player.deduction
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <p className={`${theme.total} text-sm font-semibold tabular-nums`}>
                                                        {player.total}
                                                    </p>
                                                    <p className={`${theme.textSecondary} text-[9px] tabular-nums`}>
                                                        Brutto {player.rawTotal}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            )}

            <section
                ref={leaderboardSectionRef}
                className={`${theme.section} ${theme.border} overflow-hidden rounded-2xl border shadow-sm`}
            >
                <div className={`${theme.border} border-b px-5 py-5 sm:px-7 sm:py-6`}>
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                        <div>
                            <p className={`${theme.textSecondary} text-[11px] font-semibold uppercase tracking-[0.28em]`}>
                                Officiell leaderboard
                            </p>
                            {tournament.name && (
                                <p className={`${theme.textSecondary} mt-1 text-sm`}>
                                    {tournament.name}
                                </p>
                            )}
                        </div>

                        <div className={`${theme.textSecondary} flex items-center gap-2 text-xs font-medium`}>
                            <span className={`${theme.tableRow} ${theme.border} rounded-full border px-3 py-1.5`}>
                                {sortedPlayers.length} spelare
                            </span>
                            <span className={`${theme.tableRow} ${theme.border} rounded-full border px-3 py-1.5`}>
                                {rounds.length} {rounds.length === 1 ? "runda" : "rundor"}
                            </span>
                        </div>
                    </div>
                </div>

                {finalRoundHasScores && podium.length > 0 && (
                    <div className={`${theme.sectionMuted} ${theme.border} border-b px-4 py-6 sm:px-7`}>
                        <p className={`${theme.textSecondary} mb-3 text-[10px] font-semibold uppercase tracking-[0.24em]`}>
                            Ställning
                        </p>
                        <div className="grid gap-3 sm:grid-cols-3">
                            {podium.map((player, index) => {
                                const cardClass =
                                    index === 0
                                        ? `${theme.borderStrong} ${theme.accentSoft}`
                                        : index === 1
                                            ? `${theme.border} ${theme.tableRow}`
                                            : "border-[#b88b69] bg-[#f6eee8]";

                                return (
                                    <div
                                        key={player.id}
                                        className={`rounded-xl border px-4 py-4 ${cardClass}`}
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                                <p className={`${theme.textSecondary} text-[10px] font-semibold uppercase tracking-[0.2em]`}>
                                                    {index + 1}:a plats
                                                </p>
                                                <p className={`${theme.textPrimary} mt-1 truncate text-base font-semibold`}>
                                                    {player.name}
                                                </p>
                                            </div>
                                            <div className={`${theme.primary} ${theme.textOnPrimary} flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold`}>
                                                {index + 1}
                                            </div>
                                        </div>
                                        <div className="mt-5 flex items-baseline justify-between border-t border-black/10 pt-3">
                                            <span className={`${theme.textSecondary} text-xs`}>Resultat</span>
                                            <span className={`${theme.total} text-xl font-semibold tabular-nums`}>
                                                {getTotalWithDeduction(player)}
                                                <span className={`${theme.textSecondary} ml-1 text-xs font-medium`}>
                                                    slag
                                                </span>
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}



                {!showRoundNamesInHeader && expandedRound && (
                    <div
                        className={`${theme.primarySoft} ${theme.border} hidden border-b px-4 py-3 md:block md:px-6`}
                    >
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <div className="min-w-0">
                                <p
                                    className={`${theme.textSecondary} text-[9px] font-semibold uppercase tracking-[0.2em]`}
                                >
                                    Runda {expandedRoundIndex + 1}
                                </p>

                                <p
                                    className={`${theme.heading} mt-0.5 break-words text-sm font-semibold sm:text-base`}
                                >
                                    {expandedRound?.name ||
                                        rounds[expandedRoundIndex] ||
                                        `Runda ${expandedRoundIndex + 1}`}
                                </p>
                            </div>

                            <div className="flex flex-wrap items-center gap-2">
                                <span
                                    className={`${theme.section} ${theme.border} ${theme.textPrimary} rounded-full border px-2.5 py-1 text-[10px] font-medium`}
                                >
                                    {expandedRound?.modes?.[0] ||
                                        tournamentModes?.[expandedRoundIndex] ||
                                        "Spelform saknas"}
                                </span>

                                <span
                                    className={`${theme.section} ${theme.border} ${theme.textSecondary} rounded-full border px-2.5 py-1 text-[10px] font-medium`}
                                >
                                    {expandedRound?.holes ?? 18} hål
                                </span>

                                <button
                                    type="button"
                                    onClick={() => setExpandedRoundIndex(null)}
                                    className={`${theme.textSecondary} rounded-full px-2 py-1 text-xs font-medium transition hover:bg-black/[0.04]`}
                                    aria-label="Stäng rundinformation"
                                >
                                    Stäng
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Mobil och mindre surfplattor: kortvy utan sidledsscroll. */}
                <div className="md:hidden">
                    <div className={`divide-y ${theme.border}`}>
                        {sortedPlayers.map((player, index) => {
                            const deduction = getDeduction(player);
                            const rawTotal = getRawTotal(player);
                            const total = getTotalWithDeduction(player);
                            const miniGameWins = getMiniGameWins(player);
                            const isExpanded = expandedMobilePlayerId === player.id;
                            const teamKey =
                                tournament.team_mode &&
                                tournament.team_data?.assignments?.[player.id];
                            const rowBackground =
                                index === 0
                                    ? theme.accentSoft
                                    : index % 2 === 0
                                        ? theme.tableRowAlternate
                                        : theme.tableRow;

                            return (
                                <article
                                    key={player.id}
                                    className={`${rowBackground} transition-colors`}
                                >
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setExpandedMobilePlayerId((current) =>
                                                current === player.id ? null : player.id
                                            )
                                        }
                                        className="grid w-full grid-cols-[38px_minmax(0,1fr)_56px_28px] items-center gap-2 px-3 py-3.5 text-left transition hover:bg-black/[0.025]"
                                        aria-expanded={isExpanded}
                                    >
                                        <span
                                            className={`inline-flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold tabular-nums ${
                                                index === 0
                                                    ? `${theme.primary} ${theme.textOnPrimary}`
                                                    : `${theme.border} ${theme.section} ${theme.textSecondary} border`
                                            }`}
                                        >
                                            {index + 1}
                                        </span>

                                        <div className="min-w-0">
                                            <div className="flex min-w-0 items-center gap-2">
                                                {teamKey && (
                                                    <span
                                                        className="h-2.5 w-2.5 shrink-0 rounded-full ring-2 ring-white"
                                                        style={{
                                                            backgroundColor:
                                                                teamKey === "red"
                                                                    ? "#9F3A38"
                                                                    : "#2F6B4F",
                                                        }}
                                                    />
                                                )}

                                                <span
                                                    className={`${theme.textPrimary} min-w-0 truncate text-sm font-semibold`}
                                                >
                                                    {player.name}
                                                </span>

                                            </div>

                                            <div
                                                className={`${theme.textSecondary} mt-1 flex min-h-[14px] flex-wrap items-center gap-x-2 gap-y-1 text-[10px]`}
                                            >
                                                {renderCompactStars(miniGameWins, deduction)}
                                            </div>
                                        </div>

                                        <div className="text-center">
                                            <span
                                                className={`inline-flex min-w-[48px] justify-center rounded-lg px-2.5 py-1.5 text-base font-semibold tabular-nums ${
                                                    index === 0
                                                        ? `${theme.primary} ${theme.textOnPrimary}`
                                                        : `${theme.primarySoft} ${theme.total}`
                                                }`}
                                            >
                                                {total}
                                            </span>
                                            <p className={`${theme.textSecondary} mt-1 text-[8px] font-semibold uppercase tracking-[0.12em]`}>
                                                Total
                                            </p>
                                        </div>

                                        <span
                                            aria-hidden="true"
                                            className={`${theme.accent} inline-flex h-7 w-7 items-center justify-center rounded-full transition-colors hover:bg-black/[0.035]`}
                                        >
                                            <ChevronDownIcon
                                                className={`h-4 w-4 transition-transform duration-200 ${
                                                    isExpanded ? "rotate-180" : ""
                                                }`}
                                            />
                                        </span>
                                    </button>

                                    {isExpanded && (
                                        <div className={`${theme.sectionMuted} ${theme.border} border-t px-3 pb-4 pt-3`}>
                                            <div className="mb-2 flex items-center justify-between gap-3">
                                                <p className={`${theme.textSecondary} text-[9px] font-semibold uppercase tracking-[0.18em]`}>
                                                    Rundresultat
                                                </p>
                                                <p className={`${theme.textSecondary} text-[9px]`}>
                                                    Tryck på en runda för detaljer
                                                </p>
                                            </div>

                                            <div className={`${theme.border} overflow-hidden rounded-lg border`}>
                                                {rounds.map((round, roundIndex) => {
                                                    const score = player.scores?.[roundIndex];
                                                    const numericScore = Number(score);
                                                    const hasScore =
                                                        Number.isFinite(numericScore) &&
                                                        numericScore > 0;
                                                    const isActive = activeRoundIndex === roundIndex;
                                                    const roundPosition = getRoundPosition(
                                                        player.id,
                                                        roundIndex
                                                    );
                                                    const roundMiniGameWins =
                                                        getRoundMiniGameWins(player, roundIndex);
                                                    const roundDeduction =
                                                        getRoundDeduction(player, roundIndex);
                                                    const roundGrossScore =
                                                        getRoundGrossScore(
                                                            player,
                                                            round,
                                                            roundIndex
                                                        );
                                                    const roundMode = getRoundMode(
                                                        round,
                                                        roundIndex
                                                    );
                                                    const normalizedRoundMode =
                                                        normalizeMode(roundMode);
                                                    const isScrambleRound =
                                                        normalizedRoundMode.includes(
                                                            "scramble"
                                                        );
                                                    const isHandicapRound =
                                                        isIndividualHandicapRound(
                                                            round,
                                                            roundIndex
                                                        );
                                                    const appliedHandicap =
                                                        roundGrossScore != null &&
                                                        hasScore
                                                            ? Math.max(
                                                                  0,
                                                                  roundGrossScore -
                                                                      numericScore
                                                              )
                                                            : null;
                                                    const primaryScoreLabel =
                                                        isHandicapRound
                                                            ? "Netto"
                                                            : isScrambleRound
                                                              ? round
                                                                    ?.scrambleOptions
                                                                    ?.scrambleWithHandicap
                                                                  ? "Lag netto"
                                                                  : "Lagresultat"
                                                              : "Resultat";

                                                    return (
                                                        <button
                                                            key={`${player.id}-${roundIndex}`}
                                                            type="button"
                                                            onClick={() =>
                                                                handleDetailedRoundClick(
                                                                    roundIndex,
                                                                    player.id
                                                                )
                                                            }
                                                            className={`w-full px-3 py-3 text-left transition sm:px-4 ${
                                                                roundIndex > 0
                                                                    ? `border-t ${theme.border}`
                                                                    : ""
                                                            } ${
                                                                isActive
                                                                    ? theme.primarySoft
                                                                    : `${theme.section} hover:bg-black/[0.025]`
                                                            }`}
                                                            aria-pressed={isActive}
                                                        >
                                                            <div className="flex items-start justify-between gap-3">
                                                                <div className="min-w-0 flex-1">
                                                                    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
                                                                        <span className={`${theme.textSecondary} text-[8px] font-semibold uppercase tracking-[0.14em]`}>
                                                                            Runda {roundIndex + 1}
                                                                        </span>
                                                                        {roundPosition && (
                                                                            <span className={`${theme.heading} text-[8px] font-semibold tabular-nums`}>
                                                                                · Plac. {roundPosition}
                                                                            </span>
                                                                        )}
                                                                    </div>

                                                                    <p className={`${theme.textPrimary} mt-0.5 truncate text-[12px] font-semibold`}>
                                                                        {getRoundName(round, roundIndex)}
                                                                    </p>

                                                                    <div className={`${theme.textSecondary} mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[9px]`}>
                                                                        <span>{roundMode}</span>
                                                                        <span aria-hidden="true">·</span>
                                                                        <span>{round?.holes ?? 18} hål</span>

                                                                        {isHandicapRound && (
                                                                            <>
                                                                                <span aria-hidden="true">·</span>
                                                                                <span className="tabular-nums">
                                                                                    Brutto {roundGrossScore ?? "–"}
                                                                                </span>
                                                                                <span aria-hidden="true">·</span>
                                                                                <span className={`${theme.positive} tabular-nums`}>
                                                                                    HCP {appliedHandicap != null ? `−${appliedHandicap}` : "–"}
                                                                                </span>
                                                                            </>
                                                                        )}
                                                                    </div>

                                                                    {roundMiniGameWins > 0 && (
                                                                        <div className="mt-1 min-h-[14px]">
                                                                            {renderCompactStars(
                                                                                roundMiniGameWins,
                                                                                roundDeduction
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                <div className="shrink-0 pt-0.5 text-right">
                                                                    <span className={`${theme.total} block text-base font-semibold leading-none tabular-nums`}>
                                                                        {hasScore
                                                                            ? numericScore
                                                                            : "–"}
                                                                    </span>
                                                                    <span className={`${theme.textSecondary} mt-1 block text-[8px] font-semibold uppercase tracking-[0.1em]`}>
                                                                        {primaryScoreLabel}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </button>
                                                    );
                                                })}
                                            </div>

                                            <div className={`${theme.textSecondary} mt-3 flex flex-wrap items-center gap-2 text-[9px]`}>
                                                <span className={`${theme.section} ${theme.border} rounded-full border px-2.5 py-1`}>
                                                    Brutto {rawTotal}
                                                </span>
                                                {miniGameWins > 0 && (
                                                    <span className={`${theme.section} ${theme.border} rounded-full border px-2.5 py-1`}>
                                                        {renderCompactStars(miniGameWins, deduction)}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </article>
                            );
                        })}
                    </div>
                </div>

                <div className="hidden overflow-x-auto md:block">
                    <table className="min-w-[620px] w-full border-collapse text-left text-sm">
                        <thead>
                            <tr className={theme.tableHeader}>
                                <th className="w-16 px-3 py-3 text-center text-[10px] font-semibold uppercase tracking-[0.16em] sm:px-4">
                                    Pos
                                </th>
                                <th className="min-w-[180px] px-3 py-3 text-[10px] font-semibold uppercase tracking-[0.16em] sm:px-4">
                                    Spelare
                                </th>
                                {rounds.map((round, index) => {
                                    const isExpanded =
                                        expandedRoundIndex === index;
                                    const isActive = activeRoundIndex === index;

                                    return (
                                        <th
                                            key={index}
                                            className={`${
                                                showRoundNamesInHeader
                                                    ? "min-w-[120px] max-w-[180px]"
                                                    : "min-w-[54px]"
                                            } px-2 py-2.5 text-center sm:px-3`}
                                        >
                                            {showRoundNamesInHeader ? (
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        handleDetailedRoundClick(index)
                                                    }
                                                    className={`mx-auto flex min-h-9 w-full max-w-[170px] items-center justify-center rounded-lg px-2 py-1.5 text-center text-[10px] font-semibold leading-tight transition ${
                                                        isActive
                                                            ? `${theme.accentBackground} text-white shadow-sm`
                                                            : "bg-white/10 text-white/80 hover:bg-white/20 hover:text-white"
                                                    }`}
                                                    aria-pressed={isActive}
                                                    title={getRoundName(round, index)}
                                                >
                                                    <span className="line-clamp-2 break-words">
                                                        {getRoundName(round, index)}
                                                    </span>
                                                </button>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        handleRoundHeaderClick(index)
                                                    }
                                                    className={`mx-auto inline-flex h-8 min-w-8 items-center justify-center rounded-full border px-2 text-xs font-semibold tabular-nums transition ${
                                                        isExpanded
                                                            ? `${theme.accentBackground} border-white/25 ${theme.textOnPrimary} shadow-sm`
                                                            : "border-white/20 bg-white/10 text-white/85 hover:bg-white/20 hover:text-white"
                                                    }`}
                                                    aria-expanded={isExpanded}
                                                    aria-label={`Visa information om runda ${index + 1}: ${getRoundName(round, index)}`}
                                                    title={getRoundName(round, index)}
                                                >
                                                    {index + 1}
                                                </button>
                                            )}
                                        </th>
                                    );
                                })}
                                {hasDeductions && (
                                    <th className="min-w-[76px] px-3 py-3 text-center text-[10px] font-semibold uppercase tracking-[0.14em] sm:px-4">
                                        Avdrag
                                    </th>
                                )}
                                <th
                                    className={`min-w-[82px] px-3 py-3 text-center text-[10px] font-semibold uppercase tracking-[0.14em] sm:px-4 ${
                                        isStickyTotal
                                            ? `sticky right-0 z-20 border-l border-white/10 ${theme.primary}`
                                            : ""
                                    }`}
                                >
                                    Total
                                </th>
                            </tr>


                        </thead>

                        <tbody>
                            {sortedPlayers.map((player, index) => {
                                const deduction = getDeduction(player);
                                const total = getTotalWithDeduction(player);
                                const prevPos = prevStandings.indexOf(player.id);
                                const currentPos = index;
                                const posDifference = prevPos - currentPos;
                                const hasMovementData =
                                    player.scores?.[1] !== null &&
                                    player.scores?.[1] !== 0 &&
                                    prevStandings.length > 0 &&
                                    prevPos !== -1;

                                const rowBackground =
                                    index === 0
                                        ? theme.accentSoft
                                        : index % 2 === 0
                                            ? theme.tableRowAlternate
                                            : theme.tableRow;

                                const stickyBackground =
                                    index === 0
                                        ? theme.accentSoft
                                        : index % 2 === 0
                                            ? theme.tableRowAlternate
                                            : theme.tableRow;

                                return (
                                    <tr
                                        key={player.id}
                                        className={`${rowBackground} ${theme.border} border-b transition-colors hover:bg-black/[0.025]`}
                                    >
                                        <td className="px-3 py-3 text-center sm:px-4">
                                            <span
                                                className={`inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold tabular-nums ${
                                                    index === 0
                                                        ? `${theme.primary} ${theme.textOnPrimary}`
                                                        : `${theme.border} ${theme.tableRow} ${theme.textSecondary} border`
                                                }`}
                                            >
                                                {index + 1}
                                            </span>
                                        </td>

                                        <td className="px-3 py-3 sm:px-4">
                                            <div className="flex items-center gap-2">
                                                {tournament.team_mode &&
                                                    tournament.team_data?.assignments?.[
                                                        player.id
                                                    ] && (
                                                        <span
                                                            className="h-2.5 w-2.5 shrink-0 rounded-full ring-2 ring-white"
                                                            style={{
                                                                backgroundColor:
                                                                    tournament.team_data
                                                                        .assignments[player.id] ===
                                                                    "red"
                                                                        ? "#9F3A38"
                                                                        : "#2F6B4F",
                                                            }}
                                                        />
                                                    )}

                                                <span className={`${theme.textPrimary} truncate font-medium`}>
                                                    {player.name}
                                                </span>

                                                {renderStars(getMiniGameWins(player))}

                                                {hasMovementData && currentPos < prevPos && (
                                                    <span className={`${theme.primarySoft} ${theme.positive} rounded-full px-1.5 py-0.5 text-[10px] font-semibold`}>
                                                        ↑{posDifference}
                                                    </span>
                                                )}

                                                {hasMovementData && currentPos > prevPos && (
                                                    <span className={`${theme.negative} rounded-full bg-[#f3e8e5] px-1.5 py-0.5 text-[10px] font-semibold`}>
                                                        ↓{Math.abs(posDifference)}
                                                    </span>
                                                )}
                                            </div>
                                        </td>

                                        {(player.scores || []).map((score, scoreIndex) => {
                                            const round = rounds[scoreIndex];
                                            const grossScore = getRoundGrossScore(
                                                player,
                                                round,
                                                scoreIndex
                                            );

                                            return (
                                                <td
                                                    key={scoreIndex}
                                                    className={`${theme.textSecondary} px-2 py-3 text-center font-medium tabular-nums sm:px-3`}
                                                >
                                                    <span className="block">
                                                        {score || "–"}
                                                    </span>
                                                    {grossScore != null && (
                                                        <span className={`${theme.textSecondary} mt-0.5 block whitespace-nowrap text-[9px] font-normal opacity-75`}>
                                                            Brutto {grossScore}
                                                        </span>
                                                    )}
                                                </td>
                                            );
                                        })}

                                        {hasDeductions && (
                                            <td className={`${theme.positive} px-3 py-3 text-center font-semibold tabular-nums sm:px-4`}>
                                                {deduction > 0 ? `−${deduction}` : "–"}
                                            </td>
                                        )}

                                        <td
                                            className={`px-3 py-3 text-center sm:px-4 ${
                                                isStickyTotal
                                                    ? `sticky right-0 z-10 border-l ${theme.border} ${stickyBackground}`
                                                    : ""
                                            }`}
                                        >
                                            <span
                                                className={`inline-flex min-w-[44px] justify-center rounded-md px-2.5 py-1 font-semibold tabular-nums ${
                                                    index === 0
                                                        ? `${theme.primary} ${theme.textOnPrimary}`
                                                        : `${theme.primarySoft} ${theme.total}`
                                                }`}
                                            >
                                                {total}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </section>

            <div ref={roundInfoSectionRef} className="scroll-mt-24">
                <RoundInfo
                    activeRoundIndex={activeRoundIndex}
                    setActiveRoundIndex={setActiveRoundIndex}
                    rounds={tournament.rounds}
                    players={players}
                    activeRound={activeRound}
                    tournament={tournament}
                    setTournament={setTournament}
                    expandPlayerRequest={roundInfoPlayerRequest}
                />
            </div>
        </div>
    );
}